import { Type } from '@angular/core';
import { AllowSort } from '../service/factory/allow-sort';
import { ColumnOptionsWithType } from '../service/factory/column-definition-factory.service';

export enum ColumnDataType {
  boolean = 1,
  component = 2,
  date = 3,
  dateTime = 4,
  money = 5,
  number = 6,
  text = 7,
  translation = 8,
  decimal = 9,
  truncated = 10,
  phone = 11,
  moneyOptional = 12,
}

export enum ColumnVisibility {
  mandatory = 1,
  default = 2,
  optional = 3,
}

/** Used with ColumnDataType.truncated */
export type ColumnDataOptionsTruncation = { maxLength: number };

/** Any ColumnDataOptions type */
export type ColumnDataOptions = ColumnDataOptionsTruncation; // | <other-future-option-type-2> | <other-future-option-type-3> | ...;

// IMPORTANT: keep this in sync with the definition above !!!
const columnDataOptionsKeys: (keyof ColumnDataOptions)[] = ['maxLength'];

/**
 * Moves any ColumnDataOptions properties found on 'merged' into the ColumnOptions.options property where they belong.
 * @param merged This argument must be defined!
 * @returns The 'merge' instance with the ColumnDataOptions removed and placed in the ColumnOptions.options property.
 */
export function unmergeColumnDataOptions(merged: ColumnOptionsWithType & ColumnDataOptions): ColumnOptionsWithType {
  let hasDataOpt = !!merged.options;
  const dataOpt = hasDataOpt ? merged.options : ({} as ColumnDataOptions);
  columnDataOptionsKeys.forEach(key => {
    if (key in merged) {
      hasDataOpt = true;
      dataOpt[key] = merged[key];
      delete merged[key];
    }
  });
  if (hasDataOpt) {
    merged.options = dataOpt;
  }
  return merged;
}

export function compareString(a: string, b: string): number {
  if (a) {
    return a.localeCompare(b);
  } else if (b) {
    return -1;
  } else {
    return 0;
  }
}

export function compareNumber(a: number, b: number) {
  if (a > b) {
    return 1;
  } else if (a < b) {
    return -1;
  } else {
    return 0;
  }
}

export class ColumnDefinition<TSource = any, TValue = any, TComponent = any> {
  constructor(
    public caption: string,
    readonly binding: string,
    readonly dataType?: ColumnDataType,
    readonly component?: Type<TComponent>,
    readonly visibility?: ColumnVisibility,
    readonly totalSum?: boolean,
    readonly comparer?: (a: TValue, b: TValue) => number,
    readonly helpBuilder?: (source: TSource) => string,
    readonly helpText?: string,
    readonly allowSort?: AllowSort,
    readonly serverSideSortBinding?: string,
    readonly options?: ColumnDataOptions
  ) {
    if (this.component) {
      this.dataType = ColumnDataType.component;
    } else {
      this.dataType = dataType || ColumnDataType.text;
    }
    this.visibility = visibility || ColumnVisibility.default;
    this.serverSideSortBinding = serverSideSortBinding || binding;
  }
}
